import { Box } from '@tanium/react-box';
import { Button } from '@tanium/react-button';
import { ControlGroup, ControlGroupItem } from '@tanium/react-control-group';
import styled from '@tanium/react-emotion';
import { FormRow, FormSectionBody } from '@tanium/react-form';
import { css } from '@tanium/style-system';
import { FormTextInput } from '@tanium/react-form-text-input';
import { CloseIcon } from '@tanium/react-graphics';
import { IconButton } from '@tanium/react-icon-button';
import { Notification } from '@tanium/react-persistent-notifications';
import { RadioWithLabel } from '@tanium/react-radio';
import { Shimmer } from '@tanium/react-shimmer';
import { Stack } from '@tanium/react-stack';
import { Field, FieldProps } from 'formik';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticatedApiContext } from '../../api/useApiClient';
import { useApiErrorMsgParser } from '../../hooks/useApiErrorMsgParser';
import { AsyncStatus, useAsync } from '../../hooks/useAsync';
import { MetadataType, ValidateIdpRequest } from '../../models/IdpModels';
import { MediumSubtleText } from '../../TextStyles';
import { AsyncSuspense } from '../AsyncSuspense';
import { useOnInputChange } from '../InputHandlerUtils';
import { IdpMetadataValidationBanner } from './IdpMetadataValidationBanner';

const getInitialMetadataUrl = (metadata?: ValidateIdpRequest): string => {
  return metadata?.url || '';
};

// Defined at module scope to avoid remounting the button on every render of the
// component (each render would otherwise produce a new styled-component class).
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
const EndButton = styled(Button)(
  css({
    width: '100px',
  })
);

interface IdentityProviderMetadataFormProps {
  metadata: ValidateIdpRequest;
  setMetadata: (metadata: ValidateIdpRequest | undefined) => void;
}

export const IdentityProviderMetadataForm: React.VFC<IdentityProviderMetadataFormProps> = ({
  metadata,
  setMetadata,
}) => {
  const metadataFile = metadata.xml;
  const { t } = useTranslation();
  const [validationMode, setValidationMode] = useState<MetadataType.File | MetadataType.Url>();
  const [metadataUrl, setMetadataUrl] = useState(getInitialMetadataUrl(metadata));
  // Mirrors metadataUrl synchronously so onTestMetadataUrl reads the latest value
  // even if React hasn't re-rendered yet between the input change and the click.
  const metadataUrlRef = useRef<string>(getInitialMetadataUrl(metadata));
  const [fileName, setFileName] = useState(metadataFile ? 'idp-metadata.xml' : '');
  const [urlRequiresValidation, setUrlRequiresValidation] = useState(false);
  const apiClient = useAuthenticatedApiContext();
  const validationRequest = useAsync(
    useCallback((request: ValidateIdpRequest) => apiClient.validateIdp(request), [apiClient])
  );
  const [setupType, setMetadataType] = useState<MetadataType>(MetadataType.Url);
  const onSelectMetadataType: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ currentTarget: { value } }) => {
      validationRequest.reset();
      setMetadataType(value as MetadataType);
    },
    [validationRequest]
  );

  const fileInputRef = useRef<HTMLInputElement>(null);

  const onFileAdded = useCallback(
    async (addedFile: File) => {
      setFileName(addedFile.name);
      setValidationMode(MetadataType.File);
      setMetadataUrl('');
      metadataUrlRef.current = '';
      setUrlRequiresValidation(false);

      const fileContent = await addedFile.text();

      setMetadata({ xml: fileContent });
      validationRequest.reset();
      await validationRequest.execute({ url: '', xml: fileContent });
    },
    [setMetadata, validationRequest]
  );

  const onFileRemoved = useCallback(() => {
    setMetadata(undefined);
    setFileName('');
    if (validationMode === MetadataType.File) {
      validationRequest.reset();
    }
  }, [setMetadata, validationMode, validationRequest]);

  const onChangeMedataUrl = useCallback(
    (value: string) => {
      setMetadataUrl(value);
      metadataUrlRef.current = value;
      if (value !== '' && value !== undefined) {
        onFileRemoved();
        setMetadata({ url: value });
        setUrlRequiresValidation(true);
      } else {
        setMetadata(undefined);
        setUrlRequiresValidation(false);
      }
    },
    [onFileRemoved, setMetadata]
  );

  const onTestMetadataUrl = useCallback(async () => {
    const currentUrl = metadataUrlRef.current;
    if (!currentUrl) return;
    setValidationMode(MetadataType.Url);
    setUrlRequiresValidation(false);
    onFileRemoved();

    setMetadata({ url: currentUrl });
    validationRequest.reset();
    await validationRequest.execute({ url: currentUrl, xml: '' });
  }, [onFileRemoved, setMetadata, validationRequest]);

  const onUpload = useCallback(() => {
    // This check ensures that no errors occur due to unbound reference.
    // However, given current behavior, there is no way to force this condition.
    /* istanbul ignore else */
    if (fileInputRef.current !== null) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, [fileInputRef]);

  const onSubmitInput: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
    ({ key }) => {
      /* istanbul ignore else */
      if (key === 'Enter') {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        onTestMetadataUrl();
      }
    },
    [onTestMetadataUrl]
  );

  const onUploadFile: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target }) => {
      if (target.files != null && target.files.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        onFileAdded(target.files[0]);
      }
    },
    [onFileAdded]
  );

  const onInputChange = useOnInputChange(onChangeMedataUrl);

  const validationResults = (
    <AsyncSuspense
      state={validationRequest}
      loading={<Shimmer width="100%" />}
      failure={useApiErrorMsgParser(validationRequest.error)}
    >
      <IdpMetadataValidationBanner asyncValidation={validationRequest} />
    </AsyncSuspense>
  );

  return (
    <Box>
      <FormSectionBody>
        <FormRow label={t('Method')}>
          <Stack direction="row" itemSpacing={4}>
            <RadioWithLabel
              label={t('Enter URL (Preferred)')}
              value={MetadataType.Url}
              checked={setupType === MetadataType.Url}
              onChange={onSelectMetadataType}
            ></RadioWithLabel>
            <RadioWithLabel
              label={t('Upload a Metadata File')}
              value={MetadataType.File}
              checked={setupType === MetadataType.File}
              onChange={onSelectMetadataType}
            ></RadioWithLabel>
          </Stack>
        </FormRow>

        {setupType === MetadataType.Url && (
          <Field name="samlMetadataUrl">
            {({ field, form }: FieldProps): React.ReactElement => (
              <FormRow
                required
                dataKey="metadata-url"
                label={t('Metadata URL')}
                errors={form.touched.samlMetadataUrl ? (form.errors?.samlMetadataUrl as string) : ''}
              >
                <ControlGroup>
                  <ControlGroupItem grow={1}>
                    <FormTextInput {...field} value={metadataUrl} onChange={onInputChange} onKeyDown={onSubmitInput} />
                  </ControlGroupItem>
                  <EndButton
                    onClick={onTestMetadataUrl}
                    disabled={validationRequest.status === AsyncStatus.Pending}
                  >
                    {t('Test URL')}
                  </EndButton>
                </ControlGroup>
                <Box mt={1}>
                  {urlRequiresValidation && (
                    <Notification role="status" type="warning">
                      {t('The metadata URL has changed. Click the Test URL button to validate the URL.')}
                    </Notification>
                  )}
                  {validationResults}
                </Box>
              </FormRow>
            )}
          </Field>
        )}

        {setupType === MetadataType.File && (
          <Field name="samlMetadataFile">
            {({ field, form }: FieldProps): React.ReactElement => {
              return (
                <FormRow
                  required
                  dataKey="metadata-file"
                  label={'Metadata XML File'}
                  errors={
                    /* istanbul ignore next */ form.touched.samlMetadataFile
                      ? (form.errors?.samlMetadataFile as string)
                      : ''
                  }
                  helpText={t('The identity provider metadata file is provided by your SAML identity provider')}
                >
                  <Stack direction="row" itemSpacing={1} alignItems="center">
                    <Button {...field} onClick={onUpload} disabled={validationRequest.status === AsyncStatus.Pending}>
                      {validationRequest.status === AsyncStatus.Pending ? t('Validating') : t('Upload file')}
                    </Button>
                    <MediumSubtleText>{fileName}</MediumSubtleText>
                    {fileName ? (
                      <IconButton
                        label={t('Remove file')}
                        width={16}
                        height={16}
                        icon={CloseIcon}
                        onClick={onFileRemoved}
                      ></IconButton>
                    ) : null}
                  </Stack>
                  <Box mt={1}>{validationResults}</Box>
                  <Box opacity={0} width={0} height={0}>
                    <input
                      type="file"
                      data-testid="metadata-file-upload"
                      id="metadata-file-upload"
                      ref={fileInputRef}
                      onChange={onUploadFile}
                    ></input>
                  </Box>
                </FormRow>
              );
            }}
          </Field>
        )}
      </FormSectionBody>
    </Box>
  );
};
